import React from 'react';
import { XGridToolTipCell } from '@AMIEWEB/Common';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridCellCheckbox from './cells/GridCellCheckbox';
import state from 'assets/jsons/State.json';
import moment from 'moment';
import GridCellMissingField from './cells/GridCellMissingField';
import GridCellEditWorkExperience from './cells/GridCellEditWorkExperience';
import { missingField } from 'app/constants';
import { XGridOverflowCell } from '@AMIEWEB/Common/XGrid/cells/XGridOverflowCell';

export const getWorkExperienceReadOnlyColumns = (
  t,
  classes,
  handleDisableDeleteBtn,
  onWorkExpDelete,
  showEquipmentProcedure,
) => {
  const getStateFromAbbreviation = (value: string) => {
    return state.states.find(e => e.ID === value)?.Description;
  };

  const columns: GridColDef[] = [
    {
      field: 'facilityName',
      headerName: 'Facility/Employer',
      sortable: true,
      disableColumnMenu: true,
      flex: 0,
      width: 260,
      valueFormatter: params => params.value ?? '--',
      renderCell: params => <GridCellEditWorkExperience params={params} />,
    },
    {
      field: 'country',
      headerName: 'Country',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'city',
      headerName: 'City',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'state',
      headerName: 'State/Province',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      valueFormatter: params => (params.value ? getStateFromAbbreviation(params.value) : '--'),
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'zip',
      headerName: 'Zip',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 120,
      valueFormatter: params => params.value ?? '--',
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'disciplineAbbr',
      headerName: 'Discipline',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 135,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'specialtyAbbr',
      headerName: 'Specialty',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 135,
    },
    {
      field: 'startedOn',
      headerName: 'Start Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 0,
      width: 135,
      valueFormatter: params => (moment(params.value).isValid() ? moment(params.value).format('MM/DD/YYYY') : '--'),
    },
    {
      field: 'currentlyEmployed',
      headerName: 'Currently Working',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 130,
      align: 'center',
      renderCell: params => <GridCellCheckbox params={params} />,
    },
    {
      field: 'endedOn',
      headerName: 'End Date',
      sortable: true,
      disableColumnMenu: true,
      flex: 0,
      width: 135,
      valueFormatter: params => (moment(params.value).isValid() ? moment(params.value).format('MM/DD/YYYY') : '--'),
    },
    {
      field: 'reasonForLeaving',
      headerName: 'Reason For Leaving',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      renderCell: XGridOverflowCell()(),
    },
    {
      field: 'position',
      headerName: 'Position Held',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'unit',
      headerName: 'Unit/Floor/Dept',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'numberOfBedsInFacility',
      headerName: 'Number Of Beds in Facility',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'travelCompany',
      headerName: 'Traveler Company',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      renderCell: params => DefaultGridCell(params),
    },
    {
      field: 'equipmentProcedures',
      headerName: 'Equipment/Procedures',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 150,
      align: 'center',
      renderCell: XGridOverflowCell()(),
    },
    {
      field: 'isSupervisor',
      headerName: 'Supervisor',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 120,
      align: 'center',
      renderCell: params => <GridCellCheckbox params={params} />,
    },
    {
      field: 'isTeletherapy',
      headerName: 'Teletherapy',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 120,
      align: 'center',
      renderCell: params => <GridCellCheckbox params={params} />,
    },
    {
      field: 'staffingAgency',
      headerName: 'Local Staffing Agency',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 120,
      align: 'center',
      renderCell: params => <GridCellCheckbox params={params} />,
    },
    {
      field: 'isTeachingHospital',
      headerName: 'Teaching Hospital',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      width: 120,
      align: 'center',
      renderCell: params => <GridCellCheckbox params={params} />,
    },
    {
      flex: 0,
      width: 30,
      field: 'actions',
      headerName: ' ',
      sortable: false,
      align: 'center',
      disableColumnMenu: true,
      pinnable: false,
      renderCell: XGridToolTipCell({
        tooltipContent: (params: GridRenderCellParams) => (
          <>
            <Box width={300}>
              <Typography>{t('candidate.workExperience.workExperienceGrid.disableDeleteWorkExpRecord')}</Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                {`* ${t('candidate.employmentHistoryCard.reference')}`}
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>{`* ${t('candidate.employmentHistoryCard.pid')}`}</Typography>
            </Box>
          </>
        ),
        value: (params: GridRenderCellParams) => (
          <IconButton
            color="secondary"
            size="small"
            onClick={() => onWorkExpDelete(params)}
            disabled={handleDisableDeleteBtn(params)}
          >
            <DeleteOutlineIcon
              className={handleDisableDeleteBtn(params) ? classes.disabled : classes.root}
              id="delete-candidate-work-exp"
            />
          </IconButton>
        ),
        disabled: (params: GridRenderCellParams) => {
          const deleteBtnWithoutTooltip = !handleDisableDeleteBtn(params);
          return deleteBtnWithoutTooltip;
        },
        tooltipProps: {
          placement: 'bottom-end',
          tooltipContent: undefined,
        },
      }),
    },
  ];

  return columns.filter(item => !(!showEquipmentProcedure.enabled && item.field === 'equipmentProcedures'));
};

export const MissingField = () => {
  return (
    <Typography component="span" style={{ color: '#a3a2a2' }}>
      {missingField}
    </Typography>
  );
};

export const DefaultGridCell = (params: GridRenderCellParams) => {
  return params.value == null || params.value == undefined || params.value == '' || params.value == '--' ? (
    <MissingField />
  ) : (
    params.value
  );
};
